import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { useCart } from './cart/CartContext';
import { useCartStockSync } from './cart/useCartStockSync';
import { checkoutAuthHeaders } from './checkout/checkoutAuthHeaders';
import { loadRazorpayScript } from './checkout/loadRazorpay';
import CheckoutProgress from './checkout/CheckoutProgress';
import OrderSummaryPanel from './checkout/OrderSummaryPanel';
import StoreTrustBadges from './checkout/StoreTrustBadges';
import { formatINR } from './checkout/formatINR';
import { computeShipping, shippingDeliveryNote } from './checkout/computeShipping';
import { readRecentOrderSuccess, saveOrderSuccessPayload } from './checkout/orderSuccessStorage';
import { useIndiaPincodeLookup } from './checkout/useIndiaPincodeLookup';
import { INDIAN_STATES } from './data/indianStates';
import { useProducts } from './useProducts';
import { cartLineKey } from './cart/cartUtils';
import { textPersonalizationFields } from './checkout/checkoutPersonalization';
import {
  formatCharCustomizationLabel,
  normalizePersonalizationLetter,
  productCharPersonalizationEnabled,
} from './productPersonalization';
import {
  CheckoutCoupon,
  CheckoutPaymentMethod,
  CheckoutPersonalizationFields,
  CheckoutSavedAddresses,
} from './checkout/CheckoutExtras';
import CheckoutField from './checkout/CheckoutField';
import { useSiteSettings } from './SiteSettingsContext';

function goToOrderSuccess(navigate, orderNumber, payload) {
  saveOrderSuccessPayload(payload);
  navigate(`/order-success/${encodeURIComponent(orderNumber)}`, {
    replace: true,
    state: payload,
  });
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { settings: site } = useSiteSettings();
  const store = site.store_copy || {};
  const { ready, isAuthed, displayName, email, phone, applySessionToken } = useAuth();
  const { lines, subtotal, clearCart } = useCart();
  const { products } = useProducts();
  const stockSyncEnabled = ready && lines.length > 0;
  const { stockNotice, stockLoading } = useCartStockSync({ enabled: stockSyncEnabled });
  const [savedAddressNote, setSavedAddressNote] = useState('');
  const [config, setConfig] = useState({
    razorpay_enabled: false,
    shipping_flat_inr: 0,
    shipping_rules: null,
    offline_checkout_note: '',
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [shippingName, setShippingName] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [shippingPincode, setShippingPincode] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [cityTouched, setCityTouched] = useState(false);
  const [stateTouched, setStateTouched] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('offline');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponFreeShipping, setCouponFreeShipping] = useState(false);
  const [couponMsg, setCouponMsg] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');
  const [customizations, setCustomizations] = useState({});
  const completingOrderRef = useRef(false);
  const checkoutSuccessExtrasRef = useRef({});
  /** Set after order is created but Razorpay not completed — avoids duplicate orders. */
  const pendingPaymentRef = useRef(null);
  const [pendingPaymentOrder, setPendingPaymentOrder] = useState(null);

  const personalizeFields = useMemo(() => textPersonalizationFields(lines, products), [lines, products]);

  const pinLookup = useIndiaPincodeLookup(shippingPincode);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setShippingName((prev) => prev || displayName || '');
    setShippingPhone((prev) => prev || phone || '');
  }, [displayName, phone]);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    fetch('/api/checkout_config.php', { headers: checkoutAuthHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d?.ok) return;
        const rules = d.shipping_rules || {
          mode: 'flat',
          flat_inr: Number(d.shipping_flat_inr) || 0,
          free_above_inr: 0,
          local_state: 'Gujarat',
          local_inr: Number(d.shipping_flat_inr) || 0,
          metro_inr: Number(d.shipping_flat_inr) || 0,
          remote_inr: Number(d.shipping_flat_inr) || 0,
          min_order_inr: 0,
        };
        setConfig({
          razorpay_enabled: Boolean(d.razorpay_enabled),
          shipping_flat_inr: Number(d.shipping_flat_inr) || Number(rules.flat_inr) || 0,
          shipping_rules: rules,
          offline_checkout_note: String(d.offline_checkout_note || ''),
        });
        setPaymentMethod(Boolean(d.razorpay_enabled) ? 'online' : 'offline');
        if (Array.isArray(d.saved_addresses)) setSavedAddresses(d.saved_addresses);
        const saved = d.saved_shipping;
        if (saved && typeof saved === 'object') {
          setShippingName((prev) => prev || String(saved.name || '') || displayName || '');
          setShippingPhone((prev) => prev || String(saved.phone || '') || phone || '');
          setShippingAddress((prev) => prev || String(saved.address || ''));
          setShippingCity((prev) => prev || String(saved.city || ''));
          setShippingState((prev) => prev || String(saved.state || ''));
          setShippingPincode((prev) => prev || String(saved.pincode || '').replace(/\D/g, '').slice(0, 6));
          if (saved.city || saved.pincode) setSavedAddressNote('Filled from your last order — update if needed.');
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, isAuthed, displayName, phone, email]);

  useEffect(() => {
    if (pinLookup.status !== 'ok') return;
    if (!cityTouched) setShippingCity(pinLookup.city);
    if (!stateTouched) setShippingState(pinLookup.stateName);
  }, [pinLookup.status, pinLookup.city, pinLookup.stateName, cityTouched, stateTouched]);

  const shippingRules = useMemo(
    () =>
      config.shipping_rules || {
        mode: 'flat',
        flat_inr: config.shipping_flat_inr || 0,
        free_above_inr: 0,
        local_state: 'Gujarat',
        local_inr: config.shipping_flat_inr || 0,
        metro_inr: config.shipping_flat_inr || 0,
        remote_inr: config.shipping_flat_inr || 0,
        min_order_inr: 0,
      },
    [config.shipping_rules, config.shipping_flat_inr]
  );

  const shipQuote = useMemo(() => {
    const base = computeShipping(
      shippingRules,
      subtotal,
      couponDiscount,
      shippingState || pinLookup.stateName,
      shippingPincode
    );
    if (couponFreeShipping && base.ok) {
      return {
        ...base,
        fee_inr: 0,
        zone: 'coupon',
        label: 'Free delivery (coupon)',
        free: true,
      };
    }
    return base;
  }, [
    shippingRules,
    subtotal,
    couponDiscount,
    couponFreeShipping,
    shippingState,
    shippingPincode,
    pinLookup.stateName,
  ]);

  const shipping = shipQuote.ok ? shipQuote.fee_inr : 0;
  const shippingLabelText = shipQuote.ok ? shipQuote.label : '';
  const total = Math.max(0, subtotal - couponDiscount) + (shipQuote.ok ? shipping : 0);
  const deliveryNote = shippingDeliveryNote(shipQuote, shippingRules);

  if (!ready) {
    return (
      <div className="store-page store-page--loading py-5 text-center">
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  if (lines.length === 0) {
    if (stockLoading || busy || completingOrderRef.current) {
      return (
        <div className="store-page store-page--loading py-5 text-center">
          <div className="spinner-border text-secondary" role="status" />
          <p className="small text-muted mt-3 mb-0">
            {completingOrderRef.current ? 'Confirming your order…' : 'Loading…'}
          </p>
        </div>
      );
    }
    const recentOrder = readRecentOrderSuccess();
    if (recentOrder?.orderNumber) {
      return <Navigate to={`/order-success/${encodeURIComponent(recentOrder.orderNumber)}`} replace />;
    }
    return (
      <div className="store-page checkout-page">
        <div className="container store-page__container py-5">
          <CheckoutProgress current="checkout" />
          <h1 className="store-page__title">Checkout</h1>
          <div className="alert alert-warning mt-3" role="status">
            {stockNotice
              ? stockNotice
              : store.cart_empty_lead_stock}
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <Link to="/cart" className="btn btn-outline-secondary rounded-pill">
              View bag
            </Link>
            <Link to="/shop" className="btn btn-primary rounded-pill store-cta">
              Shop collections
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const clearCoupon = () => {
    setCouponCode('');
    setCouponDiscount(0);
    setCouponFreeShipping(false);
    setCouponMsg('');
  };

  const applyCoupon = async () => {
    setCouponMsg('');
    setCouponDiscount(0);
    setCouponFreeShipping(false);
    const couponEmail = isAuthed ? email : guestEmail;
    setCouponApplying(true);
    try {
      const r = await fetch('/api/customer.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...checkoutAuthHeaders() },
        body: JSON.stringify({
          action: 'validate_coupon',
          code: couponCode.trim(),
          subtotal: Math.round(subtotal * 100) / 100,
          email: couponEmail.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setCouponMsg(d.error || 'Invalid coupon.');
        return;
      }
      setCouponDiscount(Number(d.discount) || 0);
      setCouponFreeShipping(Boolean(d.free_shipping));
      const parts = [];
      if (d.description) parts.push(d.description);
      setCouponMsg(parts.join(' ') || 'Coupon applied successfully.');
    } catch {
      setCouponMsg('Could not validate coupon. Please try again.');
    } finally {
      setCouponApplying(false);
    }
  };

  const fillAddress = (a) => {
    if (!a) return;
    setShippingName(a.name || '');
    setShippingPhone(a.phone || '');
    setShippingAddress(a.address || '');
    setShippingCity(a.city || '');
    setShippingState(a.state || '');
    setShippingPincode(String(a.pincode || '').replace(/\D/g, '').slice(0, 6));
    setSavedAddressNote(`Using saved address: ${a.label || 'Home'}`);
  };
  const successPayload = (orderNumber, paidOnline, extras = {}) => ({
    orderNumber,
    subtotal,
    shipping,
    total,
    paidOnline,
    ...extras,
  });

  const clearPendingPayment = () => {
    pendingPaymentRef.current = null;
    setPendingPaymentOrder(null);
  };

  const setPendingPayment = (orderNumber, rz, customer) => {
    pendingPaymentRef.current = { orderNumber, rz, customer };
    setPendingPaymentOrder(orderNumber);
  };

  const finishOrder = (orderNumber, paidOnline, checkoutExtras = {}) => {
    completingOrderRef.current = true;
    clearPendingPayment();
    const payload = successPayload(orderNumber, paidOnline, checkoutExtras);
    saveOrderSuccessPayload(payload);
    goToOrderSuccess(navigate, orderNumber, payload);
    clearCart();
  };

  const openRazorpayCheckout = async (orderNumber, rz, customer) => {
    const Razorpay = await loadRazorpayScript();
    const instance = new Razorpay({
      key: rz.key_id,
      amount: rz.amount_paise,
      currency: rz.currency || 'INR',
      name: 'Saloni Designs',
      description: `Order ${orderNumber}`,
      order_id: rz.order_id,
      prefill: {
        name: customer?.name || shippingName,
        email: customer?.email || email || guestEmail,
        contact: customer?.phone || shippingPhone,
      },
      theme: { color: '#5A2A39' },
      handler: async (response) => {
        const vr = await fetch('/api/orders.php', {
          method: 'POST',
          headers: checkoutAuthHeaders(),
          body: JSON.stringify({
            action: 'verify_payment',
            order_number: orderNumber,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          }),
        });
        let vd = {};
        try {
          vd = await vr.json();
        } catch {
          setError(
            `Payment may have gone through but we could not verify it. Contact us with order #${orderNumber}.`
          );
          setBusy(false);
          return;
        }
        if (!vr.ok || !vd.success) {
          setError(vd.error || `Payment verification failed for order #${orderNumber}.`);
          setBusy(false);
          return;
        }
        finishOrder(orderNumber, true, checkoutSuccessExtrasRef.current);
        setBusy(false);
      },
      modal: {
        ondismiss: () => {
          setBusy(false);
          setError(
            `Payment was not completed. Order #${orderNumber} is saved — use Pay ${formatINR(total)} below to try again.`
          );
        },
      },
    });
    instance.on('payment.failed', () => {
      setBusy(false);
      setError(
        `Payment failed. Order #${orderNumber} is saved — use the pay button below to try again, or we will contact you with payment details.`
      );
    });
    instance.open();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!shipQuote.ok) {
      setError(shipQuote.error || 'Delivery is not available for this address.');
      return;
    }

    const pending = pendingPaymentRef.current;
    if (pending?.orderNumber) {
      if (paymentMethod !== 'online') {
        setError(
          `Order #${pending.orderNumber} is waiting for online payment. Complete payment or contact us to change how you pay.`
        );
        return;
      }
      setBusy(true);
      try {
        await openRazorpayCheckout(pending.orderNumber, pending.rz, pending.customer);
      } catch {
        setError('Could not open payment. Try again or contact us with your order number.');
        setBusy(false);
      }
      return;
    }

    for (const line of lines) {
      const list = products[line.categorySlug] || [];
      const p = list.find((x) => String(x.id) === String(line.productId));
      if (p && productCharPersonalizationEnabled(p)) {
        if (!normalizePersonalizationLetter(line.customization)) {
          setError(`Choose a letter for “${line.name}” before checkout. Open the product page to select one.`);
          return;
        }
      }
    }

    for (const { key } of personalizeFields) {
      if (!String(customizations[key] || '').trim()) {
        setError('Enter personalization text for all personalized items.');
        return;
      }
    }

    setBusy(true);
    let razorpayOpened = false;
    try {
      const r = await fetch('/api/orders.php', {
        method: 'POST',
        headers: checkoutAuthHeaders(),
        body: JSON.stringify({
          action: 'create_checkout',
          items: lines.map((l) => {
            const letter = normalizePersonalizationLetter(l.customization);
            const fromCart = letter ? formatCharCustomizationLabel(letter) : '';
            const fromCheckout =
              customizations[cartLineKey(l.categorySlug, l.productId, l.customization)] || '';
            return {
              category_slug: l.categorySlug,
              product_id: l.productId,
              quantity: l.qty,
              customization: fromCart || fromCheckout,
            };
          }),
          coupon_code: couponCode.trim(),
          payment_method: paymentMethod,
          customer_notes: customerNotes.trim(),
          shipping_name: shippingName,
          shipping_phone: shippingPhone,
          shipping_address: shippingAddress,
          shipping_city: shippingCity,
          shipping_state: shippingState,
          shipping_pincode: shippingPincode,
          ...(isAuthed ? {} : { guest_email: guestEmail.trim() }),
        }),
      });
      let d = {};
      try {
        d = await r.json();
      } catch {
        setError('Invalid response from server.');
        return;
      }
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not place order.');
        return;
      }

      let guestSignedIn = false;
      if (d.session_token) {
        const sessionRes = await applySessionToken(d.session_token, true);
        if (!sessionRes?.ok) {
          setError(sessionRes?.error || 'Could not start your session. Sign in and try again.');
          return;
        }
        guestSignedIn = Boolean(d.guest_account_created);
      }
      checkoutSuccessExtrasRef.current = {
        guestEmail: guestEmail.trim(),
        guestSignedIn,
      };

      const orderNumber = d.order_number;
      const rz = d.razorpay;

      if (paymentMethod === 'online' && rz?.enabled && rz.key_id && rz.order_id) {
        razorpayOpened = true;
        setPendingPayment(orderNumber, rz, d.customer);
        try {
          await openRazorpayCheckout(orderNumber, rz, d.customer);
        } catch {
          setError(
            `Order #${orderNumber} was created but payment could not open. Use Pay below to try again.`
          );
          setBusy(false);
        }
        return;
      }

      finishOrder(orderNumber, false, {
        guestEmail: guestEmail.trim(),
        guestSignedIn,
      });
    } finally {
      if (!razorpayOpened) setBusy(false);
    }
  };

  const payLabel = busy
    ? 'Processing…'
    : config.razorpay_enabled && paymentMethod === 'online'
      ? `Pay ${formatINR(total)}`
      : `Place order · ${formatINR(total)}`;

  const pinHint =
    pinLookup.status === 'loading'
      ? 'Looking up PIN…'
      : pinLookup.status === 'ok' && pinLookup.locality
        ? `Area: ${pinLookup.locality}`
        : pinLookup.message || '';

  return (
    <div className="store-page checkout-page">
      <Helmet>
        <title>{store.seo_checkout_title}</title>
        {store.seo_checkout_description ? (
          <meta name="description" content={store.seo_checkout_description} />
        ) : null}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container store-page__container">
        <header className="store-page__header checkout-page__header">
          <CheckoutProgress current="checkout" />
          <h1 className="store-page__title">Checkout</h1>
          <p className="store-page__lead">We ship across India. Add your delivery details below — start with your PIN code.</p>
        </header>

        {stockNotice ? (
          <div className="alert alert-warning py-2 small mb-3" role="status">
            {stockNotice}
          </div>
        ) : null}

        {!config.razorpay_enabled && config.offline_checkout_note ? (
          <div className="alert alert-info py-2 small mb-3" role="status">
            {config.offline_checkout_note}
          </div>
        ) : null}

        <div className="store-layout">
          <section className="store-layout__main">
            <form id="checkout-form" className="checkout-form-card" onSubmit={onSubmit} noValidate>
              {pendingPaymentOrder ? (
                <div className="alert alert-warning checkout-form-card__alert" role="status">
                  Order <strong>#{pendingPaymentOrder}</strong> is saved and awaiting payment. Use{' '}
                  <strong>Pay {formatINR(total)}</strong> below to complete checkout.
                </div>
              ) : null}

              {error ? (
                <div className="alert alert-danger checkout-form-card__alert" role="alert">
                  {error}
                </div>
              ) : null}

              {!isAuthed ? (
                <div className="alert alert-light border small mb-3" role="note">
                  <p className="mb-2">
                    <Link to={`/login?next=${encodeURIComponent('/checkout')}`}>Sign in</Link> for faster checkout,
                    or continue as a guest below.
                  </p>
                  <p className="mb-0 text-muted">
                    After ordering you can{' '}
                    <Link to="/track-order">track your order</Link> with your order number and email — no password
                    required. We also create an account from your email; use{' '}
                    <Link to="/forgot-password">Forgot password</Link> later to sign in on any device.
                  </p>
                </div>
              ) : null}

              <fieldset className="checkout-form-section">
                <legend className="checkout-form-section__title">Contact</legend>
                <div className="row g-3">
                  <div className="col-12">
                    <CheckoutField id="ship-name" label="Full name" required>
                      <input
                        id="ship-name"
                        className="form-control checkout-input"
                        value={shippingName}
                        onChange={(e) => setShippingName(e.target.value)}
                        autoComplete="name"
                        required
                        disabled={busy}
                      />
                    </CheckoutField>
                  </div>
                  <div className="col-md-6">
                    <CheckoutField id="ship-phone" label="Mobile number" required hint="For delivery updates">
                      <input
                        id="ship-phone"
                        type="tel"
                        className="form-control checkout-input"
                        value={shippingPhone}
                        onChange={(e) => setShippingPhone(e.target.value)}
                        autoComplete="tel"
                        required
                        disabled={busy}
                      />
                    </CheckoutField>
                  </div>
                  {isAuthed && email ? (
                    <div className="col-md-6">
                      <CheckoutField id="ship-email" label="Email">
                        <input
                          id="ship-email"
                          type="email"
                          className="form-control checkout-input"
                          value={email}
                          readOnly
                          disabled
                        />
                      </CheckoutField>
                    </div>
                  ) : null}
                  {!isAuthed ? (
                    <div className="col-md-6">
                      <CheckoutField id="guest-email" label="Email" required hint="For order updates">
                        <input
                          id="guest-email"
                          type="email"
                          className="form-control checkout-input"
                          value={guestEmail}
                          onChange={(e) => setGuestEmail(e.target.value)}
                          autoComplete="email"
                          required
                          disabled={busy}
                        />
                      </CheckoutField>
                    </div>
                  ) : null}
                </div>
              </fieldset>

              <CheckoutCoupon
                code={couponCode}
                onCodeChange={setCouponCode}
                onApply={applyCoupon}
                onRemove={clearCoupon}
                busy={busy}
                applying={couponApplying}
                message={couponMsg}
                discount={couponDiscount}
                freeShipping={couponFreeShipping}
              />

              <CheckoutPaymentMethod
                razorpayEnabled={config.razorpay_enabled}
                value={paymentMethod}
                onChange={setPaymentMethod}
                disabled={busy || Boolean(pendingPaymentOrder)}
              />

              <CheckoutPersonalizationFields
                fields={personalizeFields}
                values={customizations}
                onChange={(key, val) => setCustomizations((prev) => ({ ...prev, [key]: val }))}
                disabled={busy}
              />

              <fieldset className="checkout-form-section">
                <legend className="checkout-form-section__title">Order notes</legend>
                <textarea
                  className="form-control checkout-input"
                  rows={2}
                  placeholder="Optional instructions for your order"
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  disabled={busy}
                />
              </fieldset>

              {savedAddressNote ? (
                <p className="alert alert-light border small py-2 mb-0" role="status">
                  {savedAddressNote}
                </p>
              ) : null}

              <fieldset className="checkout-form-section">
                <legend className="checkout-form-section__title">Delivery address</legend>
                <p className="checkout-form-section__intro">
                  Where should we send your order? Enter your 6-digit PIN first — we&apos;ll fill city and state when
                  possible.
                </p>
                <CheckoutSavedAddresses addresses={savedAddresses} onSelect={fillAddress} />
                <div className="row g-3">
                  <div className="col-md-4">
                    <CheckoutField
                      id="ship-pincode"
                      label="PIN code"
                      required
                      hint={pinHint || '6-digit postal PIN'}
                    >
                      <input
                        id="ship-pincode"
                        className="form-control checkout-input checkout-input--pin"
                        inputMode="numeric"
                        maxLength={6}
                        value={shippingPincode}
                        onChange={(e) => {
                          setShippingPincode(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setCityTouched(false);
                          setStateTouched(false);
                        }}
                        autoComplete="postal-code"
                        required
                        disabled={busy}
                      />
                    </CheckoutField>
                  </div>
                  <div className="col-md-4">
                    <CheckoutField id="ship-city" label="City / district" required>
                      <input
                        id="ship-city"
                        className="form-control checkout-input"
                        value={shippingCity}
                        onChange={(e) => {
                          setCityTouched(true);
                          setShippingCity(e.target.value);
                        }}
                        autoComplete="address-level2"
                        required
                        disabled={busy}
                      />
                    </CheckoutField>
                  </div>
                  <div className="col-md-4">
                    <CheckoutField id="ship-state" label="State" required>
                      <select
                        id="ship-state"
                        className="form-select checkout-input"
                        value={shippingState}
                        onChange={(e) => {
                          setStateTouched(true);
                          setShippingState(e.target.value);
                        }}
                        autoComplete="address-level1"
                        required
                        disabled={busy}
                      >
                        <option value="">Select state</option>
                        {INDIAN_STATES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </CheckoutField>
                  </div>
                </div>
                <CheckoutField id="ship-address" label="Street address" required>
                  <textarea
                    id="ship-address"
                    className="form-control checkout-input"
                    rows={3}
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    autoComplete="street-address"
                    placeholder="House no., street, landmark"
                    required
                    disabled={busy}
                  />
                </CheckoutField>
              </fieldset>
            </form>
          </section>

          <div className="store-layout__aside store-layout__aside--sticky">
            {!configLoading && !shipQuote.ok && shipQuote.error ? (
              <div className="alert alert-warning small mb-3" role="status">
                {shipQuote.error}
              </div>
            ) : null}
            <OrderSummaryPanel
              lines={lines}
              subtotal={subtotal}
              discount={couponDiscount}
              couponCode={couponDiscount > 0 || couponFreeShipping ? couponCode : ''}
              freeShippingFromCoupon={couponFreeShipping}
              shipping={shipping}
              shippingCaption={shippingLabelText}
              shippingLoading={configLoading}
              deliveryNote={configLoading ? 'Loading delivery fee…' : deliveryNote}
              editCartHref="/cart"
              primaryAction={
                <button
                  type="submit"
                  form="checkout-form"
                  className="btn btn-primary btn-lg rounded-pill w-100 store-cta d-none d-lg-flex"
                  disabled={busy || configLoading || !shipQuote.ok}
                >
                  {payLabel}
                </button>
              }
            />
            <button
              type="submit"
              form="checkout-form"
              className="btn btn-primary btn-lg rounded-pill w-100 store-cta checkout-mobile-pay d-lg-none"
              disabled={busy || configLoading || !shipQuote.ok}
            >
              {payLabel}
            </button>
            <p className="checkout-secure-note small text-muted mb-0 text-center d-none d-lg-block">
              {config.razorpay_enabled
                ? 'Secure payment powered by Razorpay'
                : 'We will contact you to confirm payment before dispatch.'}
            </p>
            <StoreTrustBadges compact />
            <Link to="/cart" className="store-continue-link">
              ← Back to bag
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
